/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldLabel, Grid } from '@cash-design-system/react'
import { Country } from '@cash-web/protos/squareup/common/countries.pb'
import { GlobalAddress } from '@cash-web/protos/squareup/common/location.pb'
import { regex } from '@cash-web/shared-util-validation'

import { FormInput } from '../FormInput'
import { HorizontalInputsWrapper } from '../HorizontalInputsWrapper'
import { getCountryText } from './getCountryText'

const EXPECTED_REGIONS = ['GB', 'US', 'CA']

const VALIDATIONS = {
  STREET: /[a-z0-9]+/i, // At least one alphanumeric
  CITY: regex.hasAlphaCharacters,
  STATE: {
    gb: regex.lettersOnly,
    us: regex.usState,
  },
  POSTAL_CODE: {
    gb: regex.gbPostcode,
    us: regex.usPostcode,
    ca: regex.caPostcode,
  },
} as const

export type AddressInputPrefill = {
  street?: string
  apartment?: string
  city?: string
  state?: string
  postalCode?: string
}

function regionDoesNotRequireState(region: Country) {
  return region === Country.GB
}

type Field = 'address_line_1' | 'address_line_2' | 'locality' | 'administrative_district_level_1' | 'postal_code'

export type FieldNames = Required<Pick<GlobalAddress, Field>>

const defaultFieldNames: FieldNames = {
  address_line_1: 'address_line_1',
  address_line_2: 'address_line_2',
  locality: 'locality',
  administrative_district_level_1: 'administrative_district_level_1',
  postal_code: 'postal_code',
}

export function AddressInput({
  countryCode,
  requiresStreetAddress,
  requiresCity,
  requiresState,
  requiresPostalCode,
  prefill,
  autoFocus = false,
  fieldNames = defaultFieldNames,
}: {
  countryCode: Country
  requiresStreetAddress: boolean
  requiresCity: boolean
  requiresState: boolean
  requiresPostalCode: boolean
  prefill?: AddressInputPrefill
  autoFocus?: boolean
  fieldNames?: FieldNames
}): React.ReactElement {
  const { t } = useTranslation()

  if (!EXPECTED_REGIONS.includes(countryCode)) {
    throw new Error(
      `AddressInput expected a region that was one of ${EXPECTED_REGIONS.join(', ')} but got ${countryCode}.`
    )
  }

  // Sometimes the region suppresses the state requirement.
  const requiresStateInfo = requiresState && !regionDoesNotRequireState(Country[countryCode])
  const countryCodeTranslationKey = countryCode.toLowerCase()
  const countryText = getCountryText(t, countryCodeTranslationKey)
  const { setValue } = useFormContext()
  const postalCodeOnly = requiresPostalCode && !requiresStreetAddress && !requiresState && !requiresCity

  return (
    <Grid rowGap="m">
      {requiresStreetAddress && (
        <>
          <FieldLabel label={countryText.streetLabel}>
            <FormInput
              name={fieldNames.address_line_1}
              autoFocus={autoFocus && !postalCodeOnly}
              rules={{
                required: { value: true, message: countryText.streetRequired },
                pattern: { value: VALIDATIONS.STREET, message: countryText.streetInvalid },
              }}
              placeholder={countryText.streetPlaceholder}
              defaultValue={prefill?.street}
            />
          </FieldLabel>
          <FormInput
            name={fieldNames.address_line_2}
            placeholder={countryText.apartmentPlaceholder}
            defaultValue={prefill?.apartment}
          />
        </>
      )}
      {requiresCity && (
        <FieldLabel label={countryText.cityLabel}>
          <FormInput
            name={fieldNames.locality}
            rules={{
              required: { value: true, message: countryText.cityRequired },
              minLength: { value: 2, message: countryText.cityInvalid },
              pattern: { value: VALIDATIONS.CITY, message: countryText.cityInvalid },
            }}
            placeholder={countryText.cityPlaceholder}
            defaultValue={prefill?.city}
          />
        </FieldLabel>
      )}
      <HorizontalInputsWrapper>
        {requiresStateInfo && (
          <FieldLabel label={countryText.stateLabel || ''}>
            <FormInput
              name={fieldNames.administrative_district_level_1}
              rules={{
                required: { value: true, message: countryText.stateRequired },
                minLength: { value: 2, message: countryText.stateInvalid },
                maxLength: { value: 2, message: countryText.stateInvalid },
                pattern: {
                  value: VALIDATIONS.STATE[countryCodeTranslationKey as keyof typeof VALIDATIONS.STATE],
                  message: countryText.stateInvalid,
                },
              }}
              placeholder={countryText.statePlaceholder}
              defaultValue={prefill?.state}
              onChange={e => setValue(fieldNames.administrative_district_level_1, e.target.value.toUpperCase())}
            />
          </FieldLabel>
        )}
        {requiresPostalCode && (
          <FieldLabel label={countryText.postalLabel}>
            <FormInput
              autoComplete="postal-code"
              name={fieldNames.postal_code}
              autoFocus={autoFocus && postalCodeOnly}
              onChange={e => setValue(fieldNames.postal_code, e.target.value.toUpperCase())}
              rules={{
                required: { value: true, message: countryText.postalRequired },
                pattern: {
                  value: VALIDATIONS.POSTAL_CODE[countryCodeTranslationKey as keyof typeof VALIDATIONS.POSTAL_CODE],
                  message: countryText.postalInvalid,
                },
              }}
              placeholder={countryText.postalPlaceholder}
              defaultValue={prefill?.postalCode}
            />
          </FieldLabel>
        )}
      </HorizontalInputsWrapper>
    </Grid>
  )
}
